// Modules:
import React, { useState, useRef, useEffect, useContext } from 'react';

// Material UI:
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Alert from '@mui/material/Alert';

// Chart.js:
import { Bar } from 'react-chartjs-2';

// Utils:
import TopicConfigContext from 'utilities/topicConfigContext';
import { formatNumber, generateAnnotations } from 'utilities/utils';
import muiTheme from '../../theme';

const BarLineMix = ({
    counties,
    json,
    sortByAxis,
    selection,
    handleDropdownSelect,
    dataUnavailable,
    setDataUnavailable,
    checkIfNull,
    styles,
    setMissingSuppressedModalOpen,
}) => {
    // // State // //
    const [chartData, setChartData] = useState({});

    // // Context // //
    const { tab: currentTab } = useContext(TopicConfigContext);

    // // Refs // //
    const chartRef = useRef();

    // // Effects: // //
    useEffect(() => {
        const loadChart = () => {
            // Reset defaults if chart loads for new data:
            setChartData({});
            setDataUnavailable(false);
            if (!json || !selection) {
                return;
            }

            const { chartConfigs } = currentTab.chartSettings;

            const getChartData = () => {
                const data = {}; // The object chart.js will be passed
                const xAxisField = currentTab.chartSettings.chartXAxisField; // x-axis labels are typically the time intervals for the graph
                const dataSetNames = currentTab.chartSettings.chartDataSets;

                // Sort labels by year:
                const sortedData = sortByAxis(json, xAxisField);
                // Extract labels for chart from data:
                data.labels = sortedData.map((item) => item[xAxisField]);

                const datasetArray = [];

                dataSetNames.forEach((setName) => {
                    const dataset = chartConfigs.find(
                        (x) => x.setName === setName
                    );
                    dataset.data = json.map((x) => x[setName]);
                    datasetArray.push(dataset);
                });

                // If all values are null, the following method will change `dataUnavailable` to true:
                const allNull = checkIfNull(datasetArray);
                if (allNull) {
                    setDataUnavailable(true);
                }

                // if data available, set color palette and put chart data into state
                datasetArray.forEach((dataset, index) => {
                    if (dataset.type === 'line') {
                        dataset.backgroundColor = 'transparent';
                        dataset.borderColor = currentTab.chartSettings
                            .highContrastPalette
                            ? muiTheme.chartPalette.highContrast[index]
                            : muiTheme.chartPalette.default[index];
                    } else {
                        dataset.backgroundColor = currentTab.chartSettings
                            .highContrastPalette
                            ? muiTheme.chartPalette.highContrast[index]
                            : muiTheme.chartPalette.default[index];
                        dataset.borderColor =
                            muiTheme.chartPalette.default[index];
                    }
                });

                data.datasets = datasetArray;

                setChartData(data);
            };

            getChartData();
        };
        loadChart();
    }, [
        selection,
        json,
        checkIfNull,
        sortByAxis,
        currentTab,
        setDataUnavailable,
    ]);

    return (
        <>
            <Grid container justify="center">
                {currentTab.chartSettings.selectable ? (
                    <FormControl sx={styles}>
                        {counties.length ? (
                            <Select
                                variant="standard"
                                value={selection}
                                displayEmpty
                                inputProps={{
                                    'aria-labelledby': 'select-by-county-text',
                                }}
                                onChange={handleDropdownSelect}
                            >
                                {counties.map((county) => (
                                    <MenuItem
                                        key={county.code}
                                        value={county.code} // will be set to null when using actual endpoint
                                    >
                                        {county.county}
                                    </MenuItem>
                                ))}
                            </Select>
                        ) : null}
                        <FormHelperText id="select-by-county-text">
                            Select County
                        </FormHelperText>
                    </FormControl>
                ) : null}
            </Grid>
            {dataUnavailable ? (
                <Grid item xs={12}>
                    <div className="data-unavailable">
                        <Alert
                            sx={styles}
                            severity="info"
                            onClick={() => setMissingSuppressedModalOpen(true)}
                        >
                            Data are not available to display for{' '}
                            {counties && counties.length
                                ? counties.find(
                                      (county) => county.code === selection
                                  ).county
                                : 'selection.'}
                            .
                        </Alert>
                    </div>
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <div className="chart-container">
                        {Object.keys(chartData).length ? (
                            <Bar
                                ref={chartRef}
                                data={chartData}
                                width={100}
                                height={50}
                                options={{
                                    plugins: {
                                        title: {
                                            display:
                                                currentTab.chartSettings
                                                    .displayChartTitle,
                                            text:
                                                currentTab.chartSettings
                                                    .chartTitle,
                                        },
                                        subtitle: {
                                            display:
                                                currentTab.chartSettings
                                                    .displayChartSubtitle ||
                                                false, // TODO:  The OR statements for these two keys are to account for a real subtitle only being in place for one topic.  Once the subtitle is given for all charts, this can be removed.
                                            text:
                                                currentTab.chartSettings
                                                    .chartSubtitle || '',
                                        },
                                        annotation: {
                                            annotations: currentTab
                                                .chartSettings
                                                .displayChartDiscontinuityGraphic
                                                ? generateAnnotations(chartData)
                                                : null,
                                        },
                                        legend: {
                                            position: 'bottom',
                                        },
                                        tooltip: {
                                            enabled: true,
                                            mode: 'index',
                                            intersect: false,
                                            callbacks: {
                                                labelColor: (context) => ({
                                                    borderColor: currentTab
                                                        .chartSettings
                                                        .highContrastPalette
                                                        ? muiTheme.chartPalette
                                                              .highContrast[
                                                              context
                                                                  .datasetIndex
                                                          ]
                                                        : muiTheme.chartPalette
                                                              .default[
                                                              context
                                                                  .datasetIndex
                                                          ],
                                                    backgroundColor: currentTab
                                                        .chartSettings
                                                        .highContrastPalette
                                                        ? muiTheme.chartPalette
                                                              .highContrast[
                                                              context
                                                                  .datasetIndex
                                                          ]
                                                        : muiTheme.chartPalette
                                                              .default[
                                                              context
                                                                  .datasetIndex
                                                          ],
                                                }),
                                                label: ({
                                                    dataIndex,
                                                    dataset,
                                                }) => {
                                                    // This solution is to differentiate between rate and non-rate data sets, as rate data sets need a trailing .0 for whole values (9 => 9.0).
                                                    // This is reliant on the 'setName' key including the word 'rate' in some capacity, but may need to be reworked to have a more consistent condition.
                                                    const isRate = dataset.setName.match(
                                                        /rate/gi
                                                    );

                                                    const value =
                                                        dataset.data[dataIndex];

                                                    return isRate
                                                        ? formatNumber(value)
                                                        : value.toLocaleString();
                                                },
                                            },
                                        },
                                    },

                                    maintainAspectRatio: false,

                                    scales: {
                                        [currentTab.chartSettings
                                            .yAxisIdLeft]: {
                                            type: 'linear',
                                            grace: '20%',
                                            beginAtZero: true,
                                            ticks: {
                                                userCallback: (value) =>
                                                    value &&
                                                    value.toLocaleString(),
                                            },
                                            position: 'left',
                                            display: true,
                                            title: {
                                                display:
                                                    currentTab.chartSettings
                                                        .displayYAxisLabel,
                                                text:
                                                    currentTab.chartSettings
                                                        .yAxisLabelLeft,
                                            },
                                        },
                                        [currentTab.chartSettings
                                            .yAxisIdRight]: {
                                            type: 'linear',
                                            grace: '20%',
                                            beginAtZero: true,
                                            ticks: {
                                                userCallback: (value) =>
                                                    value &&
                                                    value.toLocaleString(),
                                            },
                                            position: 'right',
                                            title: {
                                                display:
                                                    currentTab.chartSettings
                                                        .displayYAxisLabel,
                                                text:
                                                    currentTab.chartSettings
                                                        .yAxisLabelRight,
                                            },
                                        },
                                        x: {
                                            title: {
                                                display:
                                                    currentTab.chartSettings
                                                        .displayXAxisLabel,
                                                text:
                                                    currentTab.chartSettings
                                                        .xAxisLabel,
                                            },
                                        },
                                    },
                                }}
                            />
                        ) : null}
                    </div>
                </Grid>
            )}
        </>
    );
};

export default BarLineMix;
